import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { trackByRef } from 'src/app/lego-utils';
import { BehaviorSubject, combineLatest, Subscription, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { PMOrder, PMOrderOperation } from 'src/app/models/domain';
import { DesignService, PagedCollection, PMOperationService, PMOrderService } from 'src/app/state';
import {
  PM_OPERATION_PAGINATOR,
  providePmOperationPaginator,
} from './pm-operation-pagination.provider';

@Component({
  providers: [providePmOperationPaginator(), PMOperationService],
  selector: 'em-planning-report',
  styleUrls: ['./planning-report.component.less'],
  templateUrl: './planning-report.component.html',
  standalone: false,
})
export class PlanningReportComponent implements OnInit, OnDestroy {
  public pmOrders$ = this.pmOrderService.pmOrders$;
  public pmOperationsError$ = this.pmOperationService.error$;

  public loading$ = combineLatest([this.designService.loading$, this.pmOrderService.loading$]).pipe(
    map(([designLoading, pmOrdersLoading]) => designLoading || pmOrdersLoading)
  );

  public readonly trackByRef = trackByRef;

  public pmOrderIds: string[] = [];
  public perPage = 10;
  public pageIndex = 1;

  public perPage$: BehaviorSubject<number> = new BehaviorSubject<number>(this.perPage);
  public selectedPmOrders$: BehaviorSubject<PMOrder[]> = new BehaviorSubject<PMOrder[]>([]);

  public selectedOrders$ = this.selectedPmOrders$.pipe(
    map((pmOrders) => {
      return pmOrders.reduce((result: string[], pmOrder) => {
        if (pmOrder.orderNumber) {
          result.push(pmOrder.orderNumber);
        }

        return result;
      }, []);
    }),
    tap(() => {
      this.paginatorRef.clearCache();
    })
  );

  public pagination$ = this.perPage$.pipe(
    tap(() => {
      this.paginatorRef.clearCache();
    })
  );

  public workcenters$ = combineLatest([
    this.paginatorRef.pageChanges,
    this.selectedOrders$,
    this.pagination$,
  ]).pipe(
    switchMap(([page, pmOrderIds, perPage]) => {
      return zip(
        this.paginatorRef.getPage(() =>
          this.pmOperationService.getPage(pmOrderIds, { page, perPage })
        ),
        this.pmOrders$
      );
    }),
    map(([pmOperationResponse, orders]) => {
      return {
        ...pmOperationResponse,
        data: pmOperationResponse.data.map((operation) => ({
          ...operation,
          order: orders.find((order) => {
            return order.orderNumber && order.orderNumber === operation.orderNumber;
          }),
        })),
      };
    })
  );

  private readonly subscriptions = new Subscription();

  public constructor(
    @Inject(PM_OPERATION_PAGINATOR) public paginatorRef: PagedCollection<PMOrderOperation>,
    private readonly designService: DesignService,
    private readonly pmOrderService: PMOrderService,
    private readonly pmOperationService: PMOperationService
  ) {}

  public pageSizeChange(pageSize: number) {
    this.perPage$.next(pageSize);
  }

  public selectedPmOrdersChange(pmOrders: PMOrder[] | undefined) {
    if (pmOrders) {
      this.selectedPmOrders$.next(pmOrders);
    }
  }

  public ngOnInit(): void {
    this.subscriptions.add(this.pmOrderService.loadByActiveEquipment().subscribe());
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
